import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../components/Modal";
import { getLlmTestRun, listLlmTestRuns } from "../api";

const BTN_GHOST =
  "inline-flex items-center justify-center rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm font-semibold hover:bg-[#121825] disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";
const FILTER_SELECT =
  "rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm text-slate-200 outline-none focus:ring-2 focus:ring-violet-600/40 focus:border-violet-500/40";

const LIST_POLL_FAST_MS = 5000;
const LIST_POLL_SLOW_MS = 15000;
const LIVE_POLL_MS = 1000;
const LIVE_STICK_THRESHOLD_PX = 56;

function dash(v) {
  return v == null || String(v).trim() === "" ? "—" : String(v);
}

function pickRunId(x) {
  return String(x?.run_id ?? x?.runId ?? x?.id ?? x?._id ?? "").trim();
}

function formatDateTime(v) {
  const s = String(v || "").trim();
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatNumber(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("bg-BG").format(n);
}

function formatUsd(v, digits = 6) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)} USD`;
}

function formatBool(v) {
  if (v === true) return "Да";
  if (v === false) return "Не";
  return "—";
}

function statusClass(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "completed") return "border-emerald-600/40 bg-emerald-600/15 text-emerald-200";
  if (s === "running" || s === "in_progress") return "border-amber-600/40 bg-amber-600/15 text-amber-200";
  if (s === "failed" || s === "error") return "border-rose-600/40 bg-rose-600/15 text-rose-200";
  return "border-slate-700 bg-slate-700/30 text-slate-300";
}

function roleClass(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "assistant") return "border-violet-600/40 bg-violet-600/15 text-violet-200";
  if (r === "user") return "border-emerald-600/40 bg-emerald-600/15 text-emerald-200";
  return "border-slate-700 bg-slate-700/30 text-slate-300";
}

function isLiveStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  return s === "running" || s === "in_progress";
}

function isQueuedOrRunningStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  return s === "running" || s === "in_progress" || s === "queue" || s === "queued";
}

function buildStatusSignature(list) {
  const src = Array.isArray(list) ? list : [];
  return src
    .map((x) => `${pickRunId(x)}:${String(x?.status || "").trim().toLowerCase()}`)
    .sort()
    .join("|");
}

function buildLiveSnapshot(run) {
  const transcript = Array.isArray(run?.transcript) ? run.transcript : [];
  const last = transcript.length > 0 ? transcript[transcript.length - 1] : null;
  const lastText = String(last?.text ?? "");
  return [
    String(run?.status || "").trim().toLowerCase(),
    transcript.length,
    String(last?.idx ?? ""),
    String(last?.ts ?? ""),
    String(lastText.length),
    String(run?.error ?? ""),
  ].join("|");
}

export default function TestCasesResults() {
  const isMountedRef = useRef(true);
  const listInFlightRef = useRef(false);
  const statusSignatureRef = useRef("");
  const liveSnapshotRef = useRef("");
  const liveInFlightRef = useRef(false);
  const liveTranscriptRef = useRef(null);
  const liveStickToBottomRef = useRef(true);

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState("");

  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [hasInitialListLoaded, setHasInitialListLoaded] = useState(false);
  const [hasQueuedOrRunning, setHasQueuedOrRunning] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [detail, setDetail] = useState(null);

  const [liveOpen, setLiveOpen] = useState(false);
  const [liveRunId, setLiveRunId] = useState("");
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveErr, setLiveErr] = useState("");

  const canGoPrev = page > 1 && !loadingList;
  const canGoNext = page < totalPages && !loadingList;
  const listPollMs = hasQueuedOrRunning ? LIST_POLL_FAST_MS : LIST_POLL_SLOW_MS;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadRuns = useCallback(
    async ({ silent = false } = {}) => {
      if (listInFlightRef.current) return;
      listInFlightRef.current = true;

      try {
        if (!silent) {
          setLoadingList(true);
          setListErr("");
        }

        const result = await listLlmTestRuns({
          page,
          perPage,
        });
        if (!isMountedRef.current) return;

        const sourceItems = Array.isArray(result?.items) ? result.items : [];
        const nextHasQueuedOrRunning = sourceItems.some((x) => isQueuedOrRunningStatus(x?.status));
        setHasQueuedOrRunning((prev) => (prev === nextHasQueuedOrRunning ? prev : nextHasQueuedOrRunning));
        const normalizedItems =
          statusFilter === "all"
            ? sourceItems
            : sourceItems.filter((x) => String(x?.status || "").trim().toLowerCase() === statusFilter);

        const nextPage = Math.max(1, Number(result?.page || page));
        const nextTotalPages = Math.max(1, Number(result?.totalPages || 1));
        const nextTotal = Number.isFinite(Number(result?.total)) ? Number(result.total) : normalizedItems.length;
        const nextStatusSignature = buildStatusSignature(normalizedItems);
        const statusChanged = nextStatusSignature !== statusSignatureRef.current;
        const shouldApplyList = !silent || statusChanged;

        if (shouldApplyList) {
          setItems(normalizedItems);
          setTotal(nextTotal);
          setTotalPages(nextTotalPages);
          if (nextPage !== page) setPage(nextPage);
          statusSignatureRef.current = nextStatusSignature;
        }

        if (!silent) {
          setHasInitialListLoaded(true);
        }
        setListErr("");
      } catch (e) {
        if (!isMountedRef.current) return;
        setListErr(e?.message || "Грешка при зареждане на резултатите.");
      } finally {
        listInFlightRef.current = false;
        if (!silent && isMountedRef.current) {
          setLoadingList(false);
        }
      }
    },
    [page, perPage, statusFilter]
  );

  useEffect(() => {
    loadRuns({ silent: false });
  }, [loadRuns]);

  useEffect(() => {
    if (!hasInitialListLoaded) return;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (liveOpen) return;
      loadRuns({ silent: true });
    }, listPollMs);

    return () => clearInterval(id);
  }, [loadRuns, hasInitialListLoaded, liveOpen, listPollMs]);

  useEffect(() => {
    if (!hasInitialListLoaded) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (liveOpen) return;
      loadRuns({ silent: true });
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [loadRuns, hasInitialListLoaded, liveOpen]);

  useEffect(() => {
    if (!hasInitialListLoaded) return;
    if (liveOpen) return;
    if (document.visibilityState !== "visible") return;
    loadRuns({ silent: true });
  }, [liveOpen, hasInitialListLoaded, loadRuns]);

  useEffect(() => {
    if (!detailOpen || !selectedRunId) return;
    let alive = true;

    const run = async () => {
      try {
        setDetailLoading(true);
        setDetailErr("");
        const data = await getLlmTestRun(selectedRunId);
        if (!alive) return;
        setDetail(data || null);
      } catch (e) {
        if (!alive) return;
        setDetail(null);
        setDetailErr(e?.message || "Грешка при зареждане на детайли.");
      } finally {
        if (alive) setDetailLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [detailOpen, selectedRunId]);

  const fetchLiveRun = useCallback(
    async ({ silent = false } = {}) => {
      if (!liveRunId || liveInFlightRef.current) return null;
      liveInFlightRef.current = true;

      try {
        if (!silent) {
          setLiveLoading(true);
          setLiveErr("");
        }

        const data = await getLlmTestRun(liveRunId);
        if (!isMountedRef.current) return null;
        const next = data || null;
        const nextSnapshot = buildLiveSnapshot(next);
        if (nextSnapshot !== liveSnapshotRef.current) {
          setLiveData(next);
          liveSnapshotRef.current = nextSnapshot;
        }
        setLiveErr("");
        return next;
      } catch (e) {
        if (!isMountedRef.current) return null;
        setLiveErr(e?.message || "Грешка при зареждане на live chat.");
        return null;
      } finally {
        liveInFlightRef.current = false;
        if (!silent && isMountedRef.current) {
          setLiveLoading(false);
        }
      }
    },
    [liveRunId]
  );

  useEffect(() => {
    if (!liveOpen || !liveRunId) return;
    let tickId = null;
    let disposed = false;

    const tick = async (silent) => {
      const data = await fetchLiveRun({ silent });
      if (disposed) return;
      if (data && !isLiveStatus(data?.status) && tickId) {
        clearInterval(tickId);
        tickId = null;
      }
    };

    tick(false);
    tickId = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      tick(true);
    }, LIVE_POLL_MS);

    return () => {
      disposed = true;
      if (tickId) clearInterval(tickId);
    };
  }, [liveOpen, liveRunId, fetchLiveRun]);

  const openDetail = (row) => {
    const id = pickRunId(row);
    if (!id) return;
    setSelectedRunId(id);
    setDetailOpen(true);
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailLoading(false);
    setDetailErr("");
  };

  const openLive = (row) => {
    const id = pickRunId(row);
    if (!id) return;

    const initialLiveData = {
      run_id: id,
      status: row?.status ?? "",
      test_case_name: row?.test_case_name ?? "",
      transcript: Array.isArray(row?.transcript) ? row.transcript : [],
      error: row?.error ?? "",
    };

    setLiveRunId(id);
    setLiveData(initialLiveData);
    liveSnapshotRef.current = buildLiveSnapshot(initialLiveData);
    setLiveErr("");
    setLiveLoading(true);
    setLiveOpen(true);
    liveStickToBottomRef.current = true;
  };

  const closeLive = () => {
    setLiveOpen(false);
    setLiveLoading(false);
    setLiveErr("");
    setLiveData(null);
    setLiveRunId("");
    liveSnapshotRef.current = "";
    liveStickToBottomRef.current = true;
  };

  const transcript = useMemo(
    () => (Array.isArray(detail?.transcript) ? detail.transcript : []),
    [detail?.transcript]
  );

  const liveTranscript = useMemo(
    () => (Array.isArray(liveData?.transcript) ? liveData.transcript : []),
    [liveData?.transcript]
  );

  useEffect(() => {
    if (!liveOpen) return;
    const el = liveTranscriptRef.current;
    if (!el) return;
    if (liveStickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [liveOpen, liveTranscript.length]);

  const onLiveTranscriptScroll = () => {
    const el = liveTranscriptRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    liveStickToBottomRef.current = distanceFromBottom <= LIVE_STICK_THRESHOLD_PX;
  };

  const summary = detail?.summary && typeof detail.summary === "object" ? detail.summary : {};
  const judge = summary?.judge && typeof summary.judge === "object" ? summary.judge : {};
  const usage = summary?.usage && typeof summary.usage === "object" ? summary.usage : {};
  const showBlockingListLoading = loadingList && items.length === 0;

  return (
    <>
      <div className="card rounded-2xl p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold whitespace-nowrap">Резултати</h2>
          <div className="ml-auto flex items-center gap-2">
            <select
              className={FILTER_SELECT}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(String(e.target.value || "all"));
                setPage(1);
              }}
            >
              <option value="all">Всички</option>
              <option value="queued">queued</option>
              <option value="running">running</option>
              <option value="completed">completed</option>
              <option value="failed">failed</option>
            </select>
          </div>
        </div>

        {!!listErr && (
          <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
            {listErr}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {showBlockingListLoading ? (
            <div className="text-sm text-slate-300">Зареждане...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-300">Няма резултати за избрания филтър.</div>
          ) : (
            items.map((row, idx) => {
              const runId = pickRunId(row);
              const showLive = isLiveStatus(row?.status);
              return (
                <div
                  key={runId || `${row?.test_case_name || "run"}-${idx}`}
                  className="w-full rounded-2xl border border-[#1f2638] bg-[#0f1523] px-4 py-3 transition hover:bg-[#121825]"
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => openDetail(row)}
                      className="min-w-0 flex-1 text-left rounded-lg p-1 -m-1 outline-none focus:ring-2 focus:ring-violet-600/40"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-200 truncate">{dash(row?.test_case_name)}</span>
                        <span className="text-xs text-slate-500">#{dash(runId)}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-300 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Асистент сценарий: {dash(row?.assistant_scenario_resolved?.name)}</span>
                        <span>Потребителски сценарий: {dash(row?.user_scenario)}</span>
                        <span>Потребителско настроение: {dash(row?.user_mood)}</span>
                        <span>Клиент: {dash(row?.client_name)}</span>
                        <span>Стартиран: {formatDateTime(row?.started_at)}</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-100 truncate">{dash(row?.goal)}</div>
                    </button>

                    <div className="shrink-0 flex items-center gap-2">
                      <span
                        className={[
                          "rounded-full border px-2 py-0.5 text-xs font-medium",
                          statusClass(row?.status),
                        ].join(" ")}
                      >
                        {dash(row?.status)}
                      </span>

                      {showLive && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLive(row);
                          }}
                          className="inline-flex items-center justify-center rounded-xl border border-violet-500/40 bg-violet-600/20 px-3 py-1.5 text-xs font-semibold whitespace-nowrap text-violet-100 transition hover:bg-violet-600/30"
                        >
                          Live chat
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            className={BTN_GHOST}
            disabled={!canGoPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Назад
          </button>
          <div className="text-sm text-slate-300 px-2">
            Страница {page} / {Math.max(1, totalPages)}
          </div>
          <button
            className={BTN_GHOST}
            disabled={!canGoNext}
            onClick={() => setPage((p) => p + 1)}
          >
            Напред
          </button>
          <div className="ml-auto text-xs text-slate-400">Общо: {formatNumber(total)}</div>
        </div>
      </div>

      <Modal
        open={detailOpen}
        title="Детайли за резултат"
        onClose={closeDetail}
        scroll={false}
        panelClassName="max-w-5xl w-[95vw] md:w-[1100px]"
        footer={
          <button className={BTN_PRIMARY} onClick={closeDetail}>
            Close
          </button>
        }
      >
        <div className="max-h-[75vh] overflow-auto nice-scrollbar pr-4" style={{ scrollbarGutter: "stable both-edges" }}>
          {detailLoading ? (
            <div className="text-sm text-slate-300">Зареждане...</div>
          ) : (
            <div className="space-y-4">
              {!!detailErr && (
                <div className="rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
                  {detailErr}
                </div>
              )}

              {!!detail && (
                <>
                  <section className="rounded-xl border border-[#1f2638] bg-[#0f1523] px-2.5 py-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="rounded-lg border border-[#1f2638] bg-[#0d1424] px-2 py-1.5">
                        <div className="text-[10px] leading-none text-slate-400">input_tokens</div>
                        <div className="mt-1 text-[11px] leading-none font-semibold text-slate-200">
                          {formatNumber(usage?.input_tokens)}
                        </div>
                      </div>
                      <div className="rounded-lg border border-[#1f2638] bg-[#0d1424] px-2 py-1.5">
                        <div className="text-[10px] leading-none text-slate-400">output_tokens</div>
                        <div className="mt-1 text-[11px] leading-none font-semibold text-slate-200">
                          {formatNumber(usage?.output_tokens)}
                        </div>
                      </div>
                      <div className="rounded-lg border border-[#1f2638] bg-[#0d1424] px-2 py-1.5">
                        <div className="text-[10px] leading-none text-slate-400">total_tokens</div>
                        <div className="mt-1 text-[11px] leading-none font-semibold text-slate-200">
                          {formatNumber(usage?.total_tokens)}
                        </div>
                      </div>
                      <div className="rounded-lg border border-[#1f2638] bg-[#0d1424] px-2 py-1.5">
                        <div className="text-[10px] leading-none text-slate-400">estimated_cost_usd</div>
                        <div className="mt-1 text-[11px] leading-none font-semibold text-slate-200">
                          {formatUsd(usage?.estimated_cost_usd, 6)}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4">
                    <div className="text-sm font-semibold text-slate-200">Основни данни</div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-slate-400">Run ID</div>
                        <div className="mt-1 text-slate-200 break-all">{dash(detail?.run_id)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Статус</div>
                        <div className="mt-1">
                          <span
                            className={[
                              "rounded-full border px-2 py-0.5 text-xs font-medium",
                              statusClass(detail?.status),
                            ].join(" ")}
                          >
                            {dash(detail?.status)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Асистент сценарий</div>
                        <div className="mt-1 text-slate-200">{dash(detail?.assistant_scenario_resolved?.name)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Тест кейс</div>
                        <div className="mt-1 text-slate-200">{dash(detail?.test_case_name)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Потребителски сценарий</div>
                        <div className="mt-1 text-slate-200">{dash(detail?.user_scenario)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Потребителско настроение</div>
                        <div className="mt-1 text-slate-200">{dash(detail?.user_mood)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Клиент</div>
                        <div className="mt-1 text-slate-200 break-all">{dash(detail?.client_name)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Причина за стоп</div>
                        <div className="mt-1 text-slate-200">{dash(detail?.stop_reason)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Стартиран</div>
                        <div className="mt-1 text-slate-200">{formatDateTime(detail?.started_at)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Приключил</div>
                        <div className="mt-1 text-slate-200">{formatDateTime(detail?.ended_at)}</div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="text-xs text-slate-400">Цел</div>
                      <div className="mt-1 text-sm text-slate-200 whitespace-pre-wrap">{dash(detail?.goal)}</div>
                    </div>

                    {!!String(detail?.error || "").trim() && (
                      <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-3 py-2 text-sm text-rose-200">
                        {detail.error}
                      </div>
                    )}
                  </section>

                  <section className="rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4">
                    <div className="text-sm font-semibold text-slate-200">Обобщение</div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-slate-400">Общо реплики</div>
                        <div className="mt-1 text-slate-200">{formatNumber(summary?.total_turns)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Продължителност сек.</div>
                        <div className="mt-1 text-slate-200">{formatNumber(summary?.duration_sec)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400">Постигната цел</div>
                        <div className="mt-1 text-slate-200">{formatBool(summary?.goal_achieved)}</div>
                      </div>
                    </div>

                    <div className="mt-4 rounded-xl border border-[#1f2638] bg-[#0d1424] p-3">
                      <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Автоматична Оценка</div>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <div className="text-xs text-slate-400">Постигната цел</div>
                          <div className="mt-1 text-slate-200">{formatBool(judge?.goal_achieved)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400">Confidence оценка</div>
                          <div className="mt-1 text-slate-200">{formatNumber(judge?.confidence)}</div>
                        </div>
                        <div className="md:col-span-3">
                          <div className="text-xs text-slate-400">Причина</div>
                          <div className="mt-1 text-slate-200 whitespace-pre-wrap">{dash(judge?.reason)}</div>
                        </div>
                      </div>
                    </div>

                  </section>

                  <section className="rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4">
                    <div className="text-sm font-semibold text-slate-200">Транскрипт</div>
                    {transcript.length === 0 ? (
                      <div className="mt-3 text-sm text-slate-400">Няма транскрипт записи.</div>
                    ) : (
                      <div className="mt-3 max-h-[42vh] overflow-auto nice-scrollbar pr-2 space-y-2">
                        {transcript.map((turn, idx) => (
                          <div key={`${turn?.idx || idx}-${turn?.ts || ""}`} className="rounded-xl border border-[#1f2638] bg-[#0d1424] p-3">
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                              <span className="text-slate-400">#{formatNumber(turn?.idx || idx + 1)}</span>
                              <span
                                className={[
                                  "rounded-full border px-2 py-0.5 font-medium",
                                  roleClass(turn?.role),
                                ].join(" ")}
                              >
                                {dash(turn?.role)}
                              </span>
                              <span className="text-slate-500">{formatDateTime(turn?.ts)}</span>
                            </div>
                            <div className="mt-2 text-sm text-slate-200 whitespace-pre-wrap">{dash(turn?.text)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={liveOpen}
        title="Live chat"
        onClose={closeLive}
        scroll={false}
        panelClassName="max-w-4xl w-[95vw] md:w-[980px]"
        footer={
          <button className={BTN_PRIMARY} onClick={closeLive}>
            Затвори
          </button>
        }
      >
        <div className="max-h-[75vh] overflow-auto nice-scrollbar pr-4" style={{ scrollbarGutter: "stable both-edges" }}>
          {!!liveErr && (
            <div className="rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
              {liveErr}
            </div>
          )}

          {liveLoading && !liveData ? (
            <div className="mt-3 text-sm text-slate-300">Зареждане...</div>
          ) : (
            <div className="space-y-4">
              <section className="rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={[
                      "rounded-full border px-2 py-0.5 text-xs font-medium",
                      statusClass(liveData?.status),
                    ].join(" ")}
                  >
                    {dash(liveData?.status)}
                  </span>
                  <span className="text-sm text-slate-200">{dash(liveData?.test_case_name)}</span>
                  <span className="text-xs text-slate-500 break-all">#{dash(liveData?.run_id || liveRunId)}</span>
                </div>
              </section>

              <section className="rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4">
                <div className="text-sm font-semibold text-slate-200">Транскрипт</div>
                {liveTranscript.length === 0 ? (
                  <div className="mt-3 text-sm text-slate-400">Няма транскрипт записи.</div>
                ) : (
                  <div
                    ref={liveTranscriptRef}
                    onScroll={onLiveTranscriptScroll}
                    className="mt-3 max-h-[52vh] overflow-auto nice-scrollbar pr-2 space-y-2"
                  >
                    {liveTranscript.map((turn, idx) => (
                      <div key={`${turn?.idx || idx}-${turn?.ts || ""}`} className="rounded-xl border border-[#1f2638] bg-[#0d1424] p-3">
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          <span className="text-slate-400">#{formatNumber(turn?.idx || idx + 1)}</span>
                          <span
                            className={[
                              "rounded-full border px-2 py-0.5 font-medium",
                              roleClass(turn?.role),
                            ].join(" ")}
                          >
                            {dash(turn?.role)}
                          </span>
                          <span className="text-slate-500">{formatDateTime(turn?.ts)}</span>
                        </div>
                        <div className="mt-2 text-sm text-slate-200 whitespace-pre-wrap">{dash(turn?.text)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </Modal>

    </>
  );
}
